import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Markdown } from "../components/chat/Markdown";
import { artifactApi, SURFACE_LABEL, type SharedArtifact as Shared } from "../lib/artifactApi";
import "../styles/artifacts.css";

/**
 * Public read-only viewer for a shared artifact (FEATURE_ARTIFACTS · P2). Reached
 * at /shared/artifact/:token by anyone holding the link — no login. It fetches the
 * public endpoint (which returns ONLY title/surface/content) and renders the
 * document read-only: view + download, NO edit / promote / delete / share controls.
 * A revoked or invalid token shows a friendly "link no longer works" state.
 */
export function SharedArtifactView(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [artifact, setArtifact] = useState<Shared | null>(null);
  const [loading, setLoading] = useState(true);
  const [gone, setGone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setGone(false);
    setErr(null);
    artifactApi
      .getShared(token ?? "")
      .then((a) => {
        if (!alive) return;
        if (a === null) setGone(true);
        else setArtifact(a);
      })
      .catch((e: unknown) => {
        if (alive) setErr(e instanceof Error ? e.message : "打不开这个分享链接。");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const onDownload = (): void => {
    if (artifact === null) return;
    const blob = new Blob([artifact.content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${artifact.title || "文档"}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="shared-artifact" data-testid="shared-artifact">
      <header className="shared-artifact-head">
        <div className="shared-artifact-brand">CycleCare · 小环</div>
        {artifact !== null && (
          <button
            type="button"
            className="artifact-btn"
            data-testid="shared-artifact-download"
            onClick={onDownload}
          >
            下载
          </button>
        )}
      </header>
      <main className="shared-artifact-main">
        {loading ? (
          <div className="artifact-canvas-note">加载中…</div>
        ) : gone ? (
          <div className="shared-artifact-gone" data-testid="shared-artifact-gone">
            <h1>这个分享链接已失效</h1>
            <p>可能已被作者关闭分享或删除。如需查看，请向分享者重新索取链接。</p>
          </div>
        ) : err !== null ? (
          <div className="artifact-canvas-note" role="alert">
            {err}
          </div>
        ) : artifact !== null ? (
          <article className="shared-artifact-doc">
            <div className="shared-artifact-title">
              <h1>{artifact.title}</h1>
              <span className="artifact-canvas-sub">{SURFACE_LABEL[artifact.surface]} · 只读分享</span>
            </div>
            <div className="md artifact-canvas-doc">
              <Markdown>{artifact.content}</Markdown>
            </div>
          </article>
        ) : null}
      </main>
    </div>
  );
}
